import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { PermissionButton } from 'src/routes/components';
import type { LeverageTier, LeverageTierPayload, TranslateFn } from '../types';

const I18N_PREFIX = 'module.boldPredictionCampaign';

const tL = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

type TierDialogProps = {
  open: boolean;
  saving: boolean;
  editing?: LeverageTier | null;
  tiers: LeverageTier[];
  onClose: () => void;
  onSubmit: (payload: LeverageTierPayload) => void;
};

export default function TierDialog({
  open,
  saving,
  editing,
  tiers,
  onClose,
  onSubmit,
}: TierDialogProps) {
  const t = useTranslate();
  const isEdit = Boolean(editing);

  const [tierIndex, setTierIndex] = useState<string>('');
  const [threshold, setThreshold] = useState('');
  const [reward, setReward] = useState('');

  const existingIndexes = useMemo(() => tiers.map((tt) => tt.tierIndex), [tiers]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTierIndex(String(editing.tierIndex));
      setThreshold(String(editing.volumeThresholdUsdc ?? ''));
      setReward(String(editing.rewardUsdc ?? ''));
    } else {
      const nextIndex = (existingIndexes.length ? Math.max(...existingIndexes) : 0) + 1;
      setTierIndex(String(nextIndex));
      setThreshold('');
      setReward('');
    }
  }, [editing, existingIndexes, open]);

  const indexNum = Number(tierIndex);
  const thresholdNum = Number(threshold);

  const { prevThreshold, nextThreshold } = useMemo(() => {
    const sorted = [...tiers].sort((a, b) => a.tierIndex - b.tierIndex);
    let prev: number | null = null;
    let next: number | null = null;
    sorted.forEach((tt) => {
      if (tt.tierIndex === indexNum) return;
      if (tt.tierIndex < indexNum) {
        const v = Number(tt.volumeThresholdUsdc);
        if (Number.isFinite(v)) prev = prev === null ? v : Math.max(prev, v);
      } else if (tt.tierIndex > indexNum) {
        const v = Number(tt.volumeThresholdUsdc);
        if (Number.isFinite(v)) next = next === null ? v : Math.min(next, v);
      }
    });
    return { prevThreshold: prev, nextThreshold: next };
  }, [tiers, indexNum]);

  const indexInvalid =
    !tierIndex.trim() || !Number.isFinite(indexNum) || !Number.isInteger(indexNum) || indexNum <= 0;
  const indexDuplicate =
    !isEdit && Number.isFinite(indexNum) && existingIndexes.includes(indexNum);

  const thresholdEmpty =
    !threshold.trim() || !Number.isFinite(thresholdNum) || thresholdNum < 0;
  const thresholdBelowPrev =
    !thresholdEmpty && prevThreshold !== null && thresholdNum <= prevThreshold;
  const thresholdAboveNext =
    !thresholdEmpty && nextThreshold !== null && thresholdNum >= nextThreshold;
  const thresholdInvalid = thresholdEmpty || thresholdBelowPrev || thresholdAboveNext;

  const rewardInvalid = !reward.trim() || !Number.isFinite(Number(reward)) || Number(reward) < 0;

  const isInvalid = indexInvalid || indexDuplicate || thresholdInvalid || rewardInvalid;

  const rangeHint =
    prevThreshold !== null && nextThreshold !== null
      ? tL(t, 'tier.thresholdRangeHint', {
          prev: String(prevThreshold),
          next: String(nextThreshold),
        })
      : prevThreshold !== null
        ? tL(t, 'tier.thresholdGreaterThanHint', { value: String(prevThreshold) })
        : nextThreshold !== null
          ? tL(t, 'tier.thresholdLessThanHint', { value: String(nextThreshold) })
          : undefined;

  const thresholdHelper = thresholdEmpty
    ? t('ra.validation.required')
    : thresholdBelowPrev
      ? tL(t, 'tier.error.thresholdBelowPrev', { value: String(prevThreshold) })
      : thresholdAboveNext
        ? tL(t, 'tier.error.thresholdAboveNext', { value: String(nextThreshold) })
        : rangeHint;

  const handleSubmit = () => {
    onSubmit({
      tierIndex: indexNum,
      volumeThresholdUsdc: String(Number(threshold)),
      rewardUsdc: String(Number(reward)),
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{tL(t, isEdit ? 'tier.editTitle' : 'tier.addTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            required
            label={tL(t, 'tier.tierIndex')}
            value={tierIndex}
            error={indexInvalid || indexDuplicate}
            helperText={
              indexInvalid
                ? t('ra.validation.required')
                : indexDuplicate
                  ? tL(t, 'tier.error.duplicateIndex')
                  : undefined
            }
            onChange={(e) => setTierIndex(e.target.value.replace(/[^\d]/g, ''))}
            disabled={isEdit}
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            size="small"
            fullWidth
            required
            label={tL(t, 'tier.threshold')}
            value={threshold}
            error={thresholdInvalid}
            helperText={thresholdHelper}
            onChange={(e) => setThreshold(e.target.value.replace(/[^\d.]/g, ''))}
            InputProps={{
              endAdornment: <InputAdornment position="end">USDC</InputAdornment>,
            }}
            inputProps={{ inputMode: 'decimal' }}
          />
          <TextField
            size="small"
            fullWidth
            required
            label={tL(t, 'tier.reward')}
            value={reward}
            error={rewardInvalid}
            helperText={rewardInvalid ? t('ra.validation.required') : undefined}
            onChange={(e) => setReward(e.target.value.replace(/[^\d.]/g, ''))}
            InputProps={{
              endAdornment: <InputAdornment position="end">USDC</InputAdornment>,
            }}
            inputProps={{ inputMode: 'decimal' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('ra.action.cancel')}
        </Button>
        <PermissionButton resource="world-cup-leverage" action="write">
          <LoadingButton
            variant="contained"
            loading={saving}
            disabled={isInvalid}
            onClick={handleSubmit}
          >
            {t('ra.action.save')}
          </LoadingButton>
        </PermissionButton>
      </DialogActions>
    </Dialog>
  );
}
